/**
 * usePersonnelAppointments Hook (V88)
 *
 * V88 변경: Firestore 직접 읽기 → httpsCallable 전환
 *
 * **기능:**
 * - 인사발령 이력을 httpsCallable로 조회
 * - 특정 직원의 발령 이력 조회 (employeeId 기준)
 * - PLANNED 상태의 예약 발령 조회
 * - 로딩/에러 상태 관리
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  PersonnelAppointment,
  mapPersonnelAppointmentDoc,
} from '../types/personnelAppointment';

/**
 * usePersonnelAppointments Hook 반환 타입
 */
export interface UsePersonnelAppointmentsResult {
  /** 인사발령 목록 */
  appointments: PersonnelAppointment[];

  /** 로딩 중 여부 */
  isLoading: boolean;

  /** 에러 */
  error: Error | null;
}

/**
 * 특정 직원의 인사발령 이력 조회 Hook
 *
 * V88: Firestore 직접 읽기 → httpsCallable 전환
 *
 * @param employeeId 직원 ID
 * @returns UsePersonnelAppointmentsResult
 */
export function usePersonnelAppointmentsByEmployee(
  employeeId: string | undefined
): UsePersonnelAppointmentsResult {
  const [appointments, setAppointments] = useState<PersonnelAppointment[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // employeeId가 없는 경우
    if (!employeeId) {
      setAppointments([]);
      setIsLoading(false);
      return;
    }

    // Firebase가 초기화되지 않은 경우 (DEMO 모드)
    if (!functions) {
      setAppointments([]);
      setIsLoading(false);
      return;
    }

    // 인사발령 이력 조회
    const fetchAppointments = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // V88: httpsCallable 호출 (functions는 위에서 null 체크 완료)
        const getPersonnelAppointmentsByEmployee = httpsCallable<
          { employeeId: string },
          { success: boolean; appointments: any[] }
        >(functions!, 'getPersonnelAppointmentsByEmployee');

        const result = await getPersonnelAppointmentsByEmployee({ employeeId });

        if (result.data.success) {
          // 타입 변환
          const appointmentsList = result.data.appointments.map((item) => ({
            id: item.id,
            employeeId: item.employeeId,
            appointmentType: item.appointmentType,
            status: item.status,
            effectiveDate: item.effectiveDate,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            toPositionId: item.toPositionId,
            toTitleId: item.toTitleId,
            fromPositionId: item.fromPositionId,
            fromTitleId: item.fromTitleId,
            note: item.note,
            createdBy: item.createdBy,
            cancelledAt: item.cancelledAt,
            cancelledBy: item.cancelledBy,
            cancelReason: item.cancelReason,
          })) as PersonnelAppointment[];

          setAppointments(appointmentsList);
        }

        setIsLoading(false);
      } catch (err) {
        console.error('인사발령 이력 조회 실패:', err);
        setError(err as Error);
        setIsLoading(false);
      }
    };

    fetchAppointments();
  }, [employeeId]);

  return { appointments, isLoading, error };
}

/**
 * 최신 인사발령 조회 유틸리티
 */
export function getLatestAppointment(
  appointments: PersonnelAppointment[]
): PersonnelAppointment | null {
  if (!appointments || appointments.length === 0) {
    return null;
  }
  return appointments[0];
}

/**
 * 직위 연차 계산 유틸리티
 */
export function calculatePositionYears(
  effectiveDate: string,
  baseDate?: string
): number {
  if (!effectiveDate) {
    return 0;
  }

  const startDate = new Date(effectiveDate);
  const endDate = baseDate ? new Date(baseDate) : new Date();
  const diffMs = endDate.getTime() - startDate.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  const years = diffDays / 365;

  return Math.round(years * 10) / 10;
}

/**
 * V72: 예약된 진급 발령 건수 조회 Hook
 *
 * V88: Firestore 직접 읽기 → httpsCallable 전환
 */
export function usePlannedPromotionCount(): {
  count: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = () => {
    setRefetchTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    // Firebase가 초기화되지 않은 경우
    if (!functions) {
      setCount(0);
      setIsLoading(false);
      return;
    }

    const fetchPlannedCount = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // V88: httpsCallable 호출 (functions는 위에서 null 체크 완료)
        const getPlannedPromotionCount = httpsCallable<
          void,
          { success: boolean; count: number }
        >(functions!, 'getPlannedPromotionCount');

        const result = await getPlannedPromotionCount();

        if (result.data.success) {
          setCount(result.data.count);
        }

        setIsLoading(false);
      } catch (err) {
        console.error('예약된 진급 발령 건수 조회 실패:', err);
        setError(err as Error);
        setCount(0);
        setIsLoading(false);
      }
    };

    fetchPlannedCount();
  }, [refetchTrigger]);

  return { count, isLoading, error, refetch };
}

/**
 * V73: PLANNED 상태의 모든 인사발령 예약 조회 Hook
 *
 * V88: Firestore 직접 읽기 → httpsCallable 전환
 * V91: graceful degradation - 에러 시 빈 배열 반환, 페이지 크래시 방지
 */
export function usePlannedAppointments(): {
  appointments: PersonnelAppointment[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const [appointments, setAppointments] = useState<PersonnelAppointment[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = () => {
    setRefetchTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    // Firebase가 초기화되지 않은 경우
    if (!functions) {
      setAppointments([]);
      setIsLoading(false);
      return;
    }

    const fetchPlannedAppointments = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // V88: httpsCallable 호출 (functions는 위에서 null 체크 완료)
        const getPlannedAppointments = httpsCallable<
          void,
          { success: boolean; appointments: any[] }
        >(functions!, 'getPlannedAppointments');

        const result = await getPlannedAppointments();

        if (result.data.success) {
          const appointmentsList = result.data.appointments.map((item) => ({
            id: item.id,
            employeeId: item.employeeId,
            appointmentType: item.appointmentType,
            status: item.status,
            effectiveDate: item.effectiveDate,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            toPositionId: item.toPositionId,
            toTitleId: item.toTitleId,
            fromPositionId: item.fromPositionId,
            fromTitleId: item.fromTitleId,
            note: item.note,
            createdBy: item.createdBy,
            cancelledAt: item.cancelledAt,
            cancelledBy: item.cancelledBy,
            cancelReason: item.cancelReason,
            // V93: 조인 필드 추가 (백엔드 V92에서 반환)
            employeeName: item.employeeName,
            employeeNumber: item.employeeNumber,
            orgUnitName: item.orgUnitName,
            createdByName: item.createdByName,
            toPositionName: item.toPositionName,
            fromPositionName: item.fromPositionName,
            toTitleName: item.toTitleName,
            fromTitleName: item.fromTitleName,
            changeDescription: item.changeDescription,
          })) as PersonnelAppointment[];

          setAppointments(appointmentsList);
        }

        setIsLoading(false);
      } catch (err: any) {
        // V91: 상세 에러 로깅
        console.error('[V91] PLANNED 인사발령 예약 조회 실패:', {
          code: err?.code,
          message: err?.message,
          details: err?.details,
        });

        // V91: graceful degradation - 에러 저장하되 빈 배열로 계속 진행
        // 이렇게 하면 진급후보 산출 등 다른 기능은 정상 동작
        setError(err as Error);
        setAppointments([]);
        setIsLoading(false);

        // V91: 예약 발령 조회 실패 시 빈 배열로 처리 (진급후보 산출은 정상 동작)
      }
    };

    fetchPlannedAppointments();
  }, [refetchTrigger]);

  return { appointments, isLoading, error, refetch };
}

/**
 * V74: 예약된 인사발령 type별 카운트 조회 Hook
 *
 * V88: Firestore 직접 읽기 → httpsCallable 전환
 */
export interface PlannedAppointmentCounts {
  earlyPromotion: number;
  positionAppointment: number;
  titleAppointment: number;
  total: number;
}

export function usePlannedAppointmentCounts(): {
  counts: PlannedAppointmentCounts;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const [counts, setCounts] = useState<PlannedAppointmentCounts>({
    earlyPromotion: 0,
    positionAppointment: 0,
    titleAppointment: 0,
    total: 0,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = () => {
    setRefetchTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    // Firebase가 초기화되지 않은 경우
    if (!functions) {
      setCounts({ earlyPromotion: 0, positionAppointment: 0, titleAppointment: 0, total: 0 });
      setIsLoading(false);
      return;
    }

    const fetchPlannedCounts = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // V88: httpsCallable 호출 (functions는 위에서 null 체크 완료)
        const getPlannedAppointmentCounts = httpsCallable<
          void,
          { success: boolean; counts: PlannedAppointmentCounts }
        >(functions!, 'getPlannedAppointmentCounts');

        const result = await getPlannedAppointmentCounts();

        if (result.data.success) {
          setCounts(result.data.counts);
        }

        setIsLoading(false);
      } catch (err) {
        console.error('[V74] 예약된 인사발령 type별 카운트 조회 실패:', err);
        setError(err as Error);
        setCounts({ earlyPromotion: 0, positionAppointment: 0, titleAppointment: 0, total: 0 });
        setIsLoading(false);
      }
    };

    fetchPlannedCounts();
  }, [refetchTrigger]);

  return { counts, isLoading, error, refetch };
}
